/**
 * P44 (Feb 28, 2026) — Purge Agency Dialog.
 *
 * Two-step destructive flow:
 *   Step 1: Dry-run preview (GET-like, no deletes) — shows what would
 *           be deleted across every tenant-scoped collection.
 *   Step 2: User clicks "Permanently Delete" → calls the same endpoint
 *           with the expected_confirm_token returned by step 1.
 *
 * AZAI super-admin only (backend enforces; we still hide the button
 * for non-super-admins to keep the UI honest).
 *
 * After successful purge, the dialog closes and the parent re-fetches
 * the agencies list so the now-deleted row disappears.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Warning, Trash, X, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

const PurgeAgencyDialog = ({ open, agency, onClose, onPurged }) => {
  const [phase, setPhase] = useState('dry_run'); // 'dry_run' | 'confirming' | 'executing' | 'done'
  const [dryRunResult, setDryRunResult] = useState(null);
  const [purgeResult, setPurgeResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-fetch dry run when dialog opens
  useEffect(() => {
    if (!open || !agency) return;
    setPhase('dry_run');
    setDryRunResult(null);
    setPurgeResult(null);
    setError(null);
    setLoading(true);
    api.post('/admin/purge-agency', { agency_id: agency.id, dry_run: true })
      .then((res) => setDryRunResult(res.data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load dry-run preview'))
      .finally(() => setLoading(false));
  }, [open, agency]);

  const executeDelete = async () => {
    if (!dryRunResult) return;
    setPhase('executing');
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/admin/purge-agency', {
        agency_id: agency.id,
        dry_run: false,
        confirm: dryRunResult.expected_confirm_token,
      });
      setPurgeResult(res.data);
      setPhase('done');
      toast.success(res.data.message);
      onPurged?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Purge failed');
      setPhase('dry_run');
    } finally {
      setLoading(false);
    }
  };

  const nonZeroCollections = dryRunResult
    ? Object.entries(dryRunResult.per_collection).filter(([, v]) => (v.found || 0) > 0)
    : [];

  const dialogClose = () => {
    if (loading) return; // Don't allow close mid-delete
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={dialogClose}>
      <DialogContent className="max-w-2xl" data-testid="purge-agency-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Warning size={22} weight="fill" />
            Permanently Delete Agency
          </DialogTitle>
          <DialogDescription>
            This will remove the agency and ALL associated data — patients, claims,
            EDI submissions, eligibility checks, users, audit trail. This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {/* Agency banner */}
        {agency && (
          <div className="rounded-lg border-2 border-rose-200 bg-rose-50 p-4 my-3" data-testid="purge-agency-banner">
            <div className="text-sm text-rose-900 font-semibold">{agency.name}</div>
            <div className="text-xs text-rose-700 mt-1 grid grid-cols-2 gap-x-4">
              <div><span className="font-medium">NPI:</span> {agency.npi || '—'}</div>
              <div><span className="font-medium">Tax ID:</span> {agency.tax_id || '—'}</div>
              <div className="col-span-2 font-mono break-all"><span className="font-medium">ID:</span> {agency.id}</div>
            </div>
          </div>
        )}

        {/* Body */}
        {loading && phase === 'dry_run' && (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <ArrowsClockwise size={20} className="animate-spin mr-2" />
            Computing what would be deleted…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="purge-error">
            <Warning size={16} className="inline mr-1" weight="fill" />
            {error}
          </div>
        )}

        {dryRunResult && phase !== 'done' && (
          <div className="space-y-3" data-testid="purge-dry-run-result">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="text-2xl font-bold text-rose-700">
                  {dryRunResult.totals.records_found.toLocaleString()}
                </span>
                <span className="ml-2 text-sm text-slate-600">records will be deleted</span>
              </div>
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                Dry run — nothing deleted yet
              </Badge>
            </div>

            {nonZeroCollections.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No tenant-scoped data found.</div>
            ) : (
              <div className="rounded-lg border border-slate-200 max-h-64 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">Collection</th>
                      <th className="px-3 py-2 text-right">Rows to delete</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {nonZeroCollections.map(([coll, v]) => (
                      <tr key={coll} data-testid={`purge-row-${coll}`}>
                        <td className="px-3 py-2 font-mono text-xs">{coll}</td>
                        <td className="px-3 py-2 text-right font-semibold text-rose-700">
                          {v.found.toLocaleString()}
                          {v.error && <span className="ml-2 text-xs text-amber-700">(err: {v.error})</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {phase === 'done' && purgeResult && (
          <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="purge-done">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
              <CheckCircle size={18} weight="fill" />
              Purge complete
            </div>
            <div className="text-sm text-emerald-900">{purgeResult.message}</div>
            <div className="text-xs text-emerald-700 mt-1">
              NPI {purgeResult.agency_npi} is now free for re-registration.
            </div>
          </div>
        )}

        <DialogFooter className="flex gap-2 sm:justify-between mt-2">
          <Button
            variant="outline"
            onClick={dialogClose}
            disabled={loading}
            data-testid="purge-cancel-btn"
          >
            <X size={14} className="mr-1" />
            {phase === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {phase !== 'done' && (
            <Button
              onClick={executeDelete}
              disabled={loading || !dryRunResult || dryRunResult.totals.records_found === 0}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="purge-confirm-btn"
            >
              {loading && phase === 'executing' ? (
                <>
                  <ArrowsClockwise size={14} className="mr-1 animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash size={14} className="mr-1" weight="fill" />
                  Permanently Delete
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PurgeAgencyDialog;
